import { parseKpiFiltersConfig } from 'src/pages/KPI/utils/parseKpiFiltersConfig';
import type { CustomReport, KpiSection } from 'src/utils/gql/types';

const MAX_SELECTED_COLUMNS = 8;
const MAX_LABELED_EXAMPLES = 5;
const MAX_UNLABELED_EXAMPLES = 4;
const MAX_VALUES_PER_METRIC = 3;
const MAX_DIMENSIONS_PER_ROW = 2;
const MAX_METRICS_PER_ROW = 3;
const MAX_TABLE_ROWS = 200;
const MAX_TOTAL_SNAPSHOT_CHARS = 30_000;

type SnapshotPair = {
  label: string;
  value: string;
};

type CustomReportSnapshotSummary = {
  connectors: string[];
  selectedColumns: string[];
  dimensionLabels: string[];
  metricLabels: string[];
  rowCount: number;
  labeledExamples: string[];
  unlabeledExamples: string[];
};

type PromptSnapshotCell = {
  columnId?: unknown;
  title?: unknown;
  value?: unknown;
  isSplit?: unknown;
};

type PromptSnapshot = {
  rows?: unknown;
  columnOrder?: unknown;
};

/**
 * Heuristic: deck copy language follows what the user types in chat (not a manual control).
 */
export function inferDeckLanguageFromChat(
  userChatText: string,
  navigatorLanguageTag?: string,
): string {
  const s = userChatText.toLowerCase().trim();
  const itHits = (
    s.match(
      /\b(il|lo|la|gli|delle|dello|questo|questa|sono|perché|metriche|vendite|grafico|report|presentazione|cliente|dati)\b/gi,
    ) || []
  ).length;
  const enHits = (
    s.match(
      /\b(the|this|that|with|which|metrics|sales|chart|report|deck|slides|audience|data|presentation)\b/gi,
    ) || []
  ).length;

  if (s.length === 0) {
    const nav = (navigatorLanguageTag || '').toLowerCase();
    if (nav.startsWith('it')) return 'Italian';
    return 'English';
  }

  if (itHits === 0 && enHits === 0) {
    const nav = (navigatorLanguageTag || '').toLowerCase();
    if (nav.startsWith('it')) return 'Italian';
    return 'English';
  }

  return itHits >= enHits ? 'Italian' : 'English';
}

function humanizeIdentifier(value: string): string {
  return value
    .replace(/::/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_\.]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function cleanSnapshotValue(value: unknown): string | null {
  if (value === null || value === undefined) return null;
  const text = String(value).trim().replace(/\s+/g, ' ');
  if (!text) return null;
  if (text === '-') return null;
  if (/^(n\/a|na|null|undefined)$/i.test(text)) return null;
  return text;
}

function parsePromptSnapshot(
  config: Record<string, unknown> | null | undefined,
): {
  columnOrder: string[];
  rows: Array<
    Array<{
      columnId: string;
      title: string;
      value: string;
      isSplit: boolean;
    }>
  >;
} | null {
  const snapshot = config?.promptSnapshot as PromptSnapshot | undefined;
  if (!snapshot || typeof snapshot !== 'object') return null;
  if (!Array.isArray(snapshot.rows)) return null;

  const rows = snapshot.rows
    .map((row) => {
      if (!Array.isArray(row)) return null;
      const parsedCells = row
        .map((cell) => {
          const record = cell as PromptSnapshotCell;
          const columnId =
            typeof record?.columnId === 'string' ? record.columnId : null;
          const title = typeof record?.title === 'string' ? record.title : null;
          const value = cleanSnapshotValue(record?.value);
          const isSplit = Boolean(record?.isSplit);

          if (!columnId || !title || value === null) return null;
          return { columnId, title, value, isSplit };
        })
        .filter(
          (
            cell,
          ): cell is {
            columnId: string;
            title: string;
            value: string;
            isSplit: boolean;
          } => cell !== null,
        );

      return parsedCells.length > 0 ? parsedCells : null;
    })
    .filter(
      (
        row,
      ): row is Array<{
        columnId: string;
        title: string;
        value: string;
        isSplit: boolean;
      }> => row !== null,
    );

  if (rows.length === 0) return null;

  const columnOrder = Array.isArray(snapshot.columnOrder)
    ? snapshot.columnOrder.filter(
        (item): item is string => typeof item === 'string' && item.length > 0,
      )
    : [];

  return { columnOrder, rows };
}

function buildPromptSnapshotTable(
  config: Record<string, unknown> | null | undefined,
): string[] | null {
  const snapshot = parsePromptSnapshot(config);
  if (!snapshot) return null;

  const columnMeta = new Map<string, { title: string; isSplit: boolean }>();
  snapshot.rows.forEach((row) => {
    row.forEach((cell) => {
      if (!columnMeta.has(cell.columnId)) {
        columnMeta.set(cell.columnId, {
          title: cell.title,
          isSplit: cell.isSplit,
        });
      }
    });
  });

  const orderedColumns = [
    ...snapshot.columnOrder.filter((columnId) => columnMeta.has(columnId)),
    ...Array.from(columnMeta.keys()).filter(
      (columnId) => !snapshot.columnOrder.includes(columnId),
    ),
  ];

  const headers = orderedColumns.map(
    (columnId) =>
      columnMeta.get(columnId)?.title || humanizeIdentifier(columnId),
  );
  const limitedRows = snapshot.rows.slice(0, MAX_TABLE_ROWS);
  const tableLines = limitedRows.map((row) => {
    const rowMap = new Map(row.map((cell) => [cell.columnId, cell.value]));
    return orderedColumns
      .map((columnId) => rowMap.get(columnId) ?? '-')
      .join(' | ');
  });

  return [
    `  Saved prompt snapshot rows: ${snapshot.rows.length}`,
    `  Table columns: ${headers.join(' | ')}`,
    '  Table rows:',
    ...tableLines.map((line) => `  ${line}`),
  ];
}

function extractPairs(
  items: unknown,
  fallbackLabelKind: 'dimension' | 'metric',
): SnapshotPair[] {
  if (!Array.isArray(items)) return [];

  return items
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const key =
        'label' in item && typeof item.label === 'string' && item.label
          ? item.label
          : 'key' in item && typeof item.key === 'string'
            ? item.key
            : fallbackLabelKind;
      const raw = 'value' in item ? cleanSnapshotValue(item.value) : null;
      if (!raw) return null;
      return {
        label: humanizeIdentifier(key),
        value: raw,
      };
    })
    .filter((item): item is SnapshotPair => item !== null);
}

function buildCustomReportSnapshotSummary(
  config: Record<string, unknown> | null | undefined,
): CustomReportSnapshotSummary {
  const emptySummary: CustomReportSnapshotSummary = {
    connectors: [],
    selectedColumns: [],
    dimensionLabels: [],
    metricLabels: [],
    rowCount: 0,
    labeledExamples: [],
    unlabeledExamples: [],
  };

  if (!config || typeof config !== 'object') return emptySummary;

  const selectedColumns = Array.isArray(config.columnOrder)
    ? config.columnOrder
        .map((item) =>
          typeof item === 'string' ? humanizeIdentifier(item) : null,
        )
        .filter((item): item is string => Boolean(item))
        .slice(0, MAX_SELECTED_COLUMNS)
    : [];

  const grouped = config.metricValuesRowsGroupedByConnector;
  if (!grouped || typeof grouped !== 'object') {
    return { ...emptySummary, selectedColumns };
  }

  const connectorSet = new Set<string>();
  const dimensionSet = new Set<string>();
  const metricSet = new Set<string>();
  const labeledExamples: string[] = [];
  const unlabeledMetricValues = new Map<string, string[]>();
  let rowCount = 0;

  for (const [connectorType, metricValuesRows] of Object.entries(grouped)) {
    if (!Array.isArray(metricValuesRows)) continue;
    connectorSet.add(humanizeIdentifier(connectorType));

    for (const row of metricValuesRows) {
      if (!row || typeof row !== 'object') continue;
      const record = row as {
        splitValues?: unknown;
        metricValues?: unknown;
      };

      const dimensions = extractPairs(record.splitValues, 'dimension');
      const metrics = extractPairs(record.metricValues, 'metric');

      if (dimensions.length === 0 && metrics.length === 0) continue;
      rowCount += 1;

      dimensions.forEach((pair) => dimensionSet.add(pair.label));
      metrics.forEach((pair) => metricSet.add(pair.label));

      if (
        dimensions.length > 0 &&
        metrics.length > 0 &&
        labeledExamples.length < MAX_LABELED_EXAMPLES
      ) {
        const dimensionText = dimensions
          .slice(0, MAX_DIMENSIONS_PER_ROW)
          .map((pair) => `${pair.label}: ${pair.value}`)
          .join(' | ');
        const metricText = metrics
          .slice(0, MAX_METRICS_PER_ROW)
          .map((pair) => `${pair.label}: ${pair.value}`)
          .join(' | ');

        labeledExamples.push(
          `  - ${humanizeIdentifier(connectorType)} | ${dimensionText} | ${metricText}`,
        );
        continue;
      }

      for (const metric of metrics) {
        const key = `${connectorType}::${metric.label}`;
        const current = unlabeledMetricValues.get(key) ?? [];
        if (
          current.length < MAX_VALUES_PER_METRIC &&
          !current.includes(metric.value)
        ) {
          current.push(metric.value);
          unlabeledMetricValues.set(key, current);
        }
      }
    }
  }

  const unlabeledExamples = Array.from(unlabeledMetricValues.entries())
    .slice(0, MAX_UNLABELED_EXAMPLES)
    .map(([key, values]) => {
      const [connectorType, label] = key.split('::');
      return `  - ${humanizeIdentifier(connectorType)} | ${label}: sample saved values ${values.join(', ')}`;
    });

  return {
    connectors: Array.from(connectorSet),
    selectedColumns,
    dimensionLabels: Array.from(dimensionSet),
    metricLabels: Array.from(metricSet),
    rowCount,
    labeledExamples,
    unlabeledExamples,
  };
}

export function summarizeCustomReportBlock(report: CustomReport): string {
  const name = report.name?.trim() || 'Untitled report';
  const config =
    report.config && typeof report.config === 'object'
      ? (report.config as Record<string, unknown>)
      : null;

  const lines: string[] = [`Custom report: "${name}"`];
  const promptSnapshotTable = buildPromptSnapshotTable(config);

  if (promptSnapshotTable) {
    lines.push(...promptSnapshotTable);
    return lines.join('\n');
  }

  const summary = buildCustomReportSnapshotSummary(config);

  if (summary.selectedColumns.length > 0) {
    lines.push(`  Selected columns: ${summary.selectedColumns.join(', ')}`);
  }
  if (summary.connectors.length > 0) {
    lines.push(`  Connectors: ${summary.connectors.join(', ')}`);
  }
  if (summary.dimensionLabels.length > 0) {
    lines.push(
      `  Dimensions visible in saved rows: ${summary.dimensionLabels.join(', ')}`,
    );
  }
  if (summary.metricLabels.length > 0) {
    lines.push(
      `  Metrics visible in saved rows: ${summary.metricLabels.join(', ')}`,
    );
  }

  if (summary.rowCount === 0) {
    lines.push(
      '  Saved snapshot: not usable for export. This report was likely saved before prompt snapshots were added. Open the report in Kipify and save it once again before exporting.',
    );
  } else if (summary.labeledExamples.length > 0) {
    lines.push(`  Saved snapshot rows: ${summary.rowCount}`);
    lines.push('  Best explicit facts from the saved snapshot:');
    lines.push(...summary.labeledExamples);
  }

  if (summary.unlabeledExamples.length > 0) {
    lines.push(
      '  Additional saved values without row labels. Use them only as raw evidence, not for segment comparisons:',
    );
    lines.push(...summary.unlabeledExamples);
  }

  return lines.join('\n');
}

export function summarizeKpiSectionBlock(section: KpiSection): string {
  const name = section.name?.trim() || 'Untitled section';
  const cfg =
    section.config && typeof section.config === 'object'
      ? (section.config as Record<string, unknown>)
      : {};

  const lines: string[] = [`KPI section: "${name}"`];

  const metrics = cfg.selectedMetrics;
  if (Array.isArray(metrics) && metrics.length > 0) {
    const labels = metrics.map(
      (m: { metric?: { label?: string; key?: string } }) => {
        const lbl = m?.metric?.label;
        const key = m?.metric?.key;
        return typeof lbl === 'string' && lbl ? lbl : String(key || 'metric');
      },
    );
    lines.push(`  Metrics: ${labels.join(', ')}`);
  } else {
    lines.push('  Metrics: (none listed in saved config)');
  }

  if (
    cfg.dateRange &&
    typeof cfg.dateRange === 'object' &&
    cfg.dateRange !== null &&
    'from' in cfg.dateRange &&
    'to' in cfg.dateRange
  ) {
    const dr = cfg.dateRange as { from?: string; to?: string };
    lines.push(`  Date range (saved): ${dr.from ?? '?'} → ${dr.to ?? '?'}`);
  }
  if (typeof cfg.dateRangePreset === 'string' && cfg.dateRangePreset) {
    lines.push(`  Date preset: ${cfg.dateRangePreset}`);
  }
  if (
    typeof cfg.compareMode === 'string' &&
    cfg.compareMode &&
    cfg.compareMode !== 'none'
  ) {
    lines.push(`  Compare mode: ${cfg.compareMode}`);
    if (
      cfg.compareDateRange &&
      typeof cfg.compareDateRange === 'object' &&
      cfg.compareDateRange !== null &&
      'from' in cfg.compareDateRange &&
      'to' in cfg.compareDateRange
    ) {
      const cdr = cfg.compareDateRange as { from?: string; to?: string };
      lines.push(`  Compare range: ${cdr.from ?? '?'} → ${cdr.to ?? '?'}`);
    }
  }

  const filters = parseKpiFiltersConfig(cfg.kpiFilters as never);
  const filterBits: string[] = [];
  if (filters.countries.length)
    filterBits.push(`countries: ${filters.countries.join(', ')}`);
  if (filters.customerType)
    filterBits.push(`customer type: ${filters.customerType}`);
  if (filters.tags.length) filterBits.push(`tags: ${filters.tags.join(', ')}`);
  if (filters.collections.length)
    filterBits.push(`collections: ${filters.collections.join(', ')}`);
  if (filters.vendors.length)
    filterBits.push(`vendors: ${filters.vendors.join(', ')}`);
  if (filters.productTypes.length)
    filterBits.push(`product types: ${filters.productTypes.join(', ')}`);
  if (filterBits.length) {
    lines.push(`  Filters: ${filterBits.join('; ')}`);
  }

  lines.push(
    '  Use this section as scope and narrative framing. Cite numbers only when they are explicitly present in the saved custom report snapshot or other visible evidence.',
  );

  return lines.join('\n');
}

export function buildStudioExportPrompt(input: {
  numSlides: number;
  deckLanguageLabel: string;
  customReports: CustomReport[];
  kpiSections: KpiSection[];
  kpiSectionBlocks?: string[];
  chatTranscript: string;
}): string {
  const parts: string[] = [];

  parts.push('Build one presentation deck from the selected Kipify sources.');
  parts.push(`Target length: up to ${input.numSlides} slides.`);
  parts.push(`Preferred output language: ${input.deckLanguageLabel}.`);
  parts.push(
    'Follow the user chat for tone, narrative style, and language. Keep the flow presentation-ready.',
  );
  parts.push(
    'Use only explicit values found in the data blocks. If evidence is missing, state it clearly.',
  );
  parts.push('');

  if (input.customReports.length > 0) {
    parts.push('### Data: Custom reports');
    const blocks: string[] = [];
    let total = 0;
    let truncated = false;
    for (const r of input.customReports) {
      const block = summarizeCustomReportBlock(r);
      const sep = blocks.length > 0 ? 2 : 0;
      if (total + sep + block.length > MAX_TOTAL_SNAPSHOT_CHARS) {
        truncated = true;
        break;
      }
      blocks.push(block);
      total += sep + block.length;
    }
    parts.push(blocks.join('\n\n'));
    if (truncated) {
      parts.push(
        '\n[Truncated: too much saved report data. Deselect some reports.]',
      );
    }
  }

  const hasKpiBlocks =
    Array.isArray(input.kpiSectionBlocks) && input.kpiSectionBlocks.length > 0;
  if (hasKpiBlocks || input.kpiSections.length > 0) {
    parts.push('');
    parts.push('### Data: KPI sections');
    if (hasKpiBlocks) {
      parts.push(input.kpiSectionBlocks!.join('\n\n'));
    } else {
      parts.push(
        input.kpiSections.map((s) => summarizeKpiSectionBlock(s)).join('\n\n'),
      );
    }
  }

  const chatBrief = input.chatTranscript.trim();
  if (chatBrief) {
    parts.push('');
    parts.push('### Chat brief');
    parts.push(chatBrief);
  }

  parts.push('');
  parts.push('Generate the deck now.');

  return parts.join('\n');
}

export function buildDevApiHintJson(input: {
  numSlides: number;
  deckLanguageLabel: string;
  customReportIds: string[];
  kpiSectionIds: string[];
}): Record<string, unknown> {
  return {
    note: 'Future server-to-server payload sketch (not wired yet).',
    body: {
      format: 'presentation',
      numSlides: input.numSlides,
      textOptions: { inferredLanguage: input.deckLanguageLabel },
      sources: {
        customReportIds: input.customReportIds,
        kpiSectionIds: input.kpiSectionIds,
      },
    },
  };
}
